import { useState, useRef, useCallback, useEffect } from 'react';
import { FileValidationResult, validateFile } from './utils';
import Container from '../../components/container/Container';
import PrimaryButton from '../../components/buttons/PrimaryButton';
import SecondaryButton from '../../components/buttons/SecondaryButton';
import { useTranslation } from 'react-i18next';
import { exitApp } from '../../hooks/utils';
import { useHistory } from 'react-router';
import FileComponent from '../../components/fileComponent/FileComponent';
import { useStepsState } from '../../hooks/zustandHooks';
import FeatherIcon from 'feather-icons-react';
import BackButton from '../../components/buttons/BackButton';

const MAX_FILES = 12;

const DocumentUpload = () => {
  const { t } = useTranslation();
  const [files, setFiles] = useState<FileValidationResult[]>([]);
  const [validating, setValidating] = useState(false);
  const [fileLimitError, setFileLimitError] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const history = useHistory();
  const setActiveStep = useStepsState(state => state.setActiveStep);

  useEffect(() => {
    setActiveStep({ stepIndex: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const processFiles = useCallback(async (incoming: FileList) => {
    setValidating(true);
    setFileLimitError(false);
    const results = await Promise.all([...incoming].map(validateFile));

    setFiles(prev => {
      const existingNames = new Set(prev.map(f => f.file.name));
      const newFiles = results.filter(r => !existingNames.has(r.file.name));
      const available = MAX_FILES - prev.length;
      const filesToAdd = newFiles.slice(0, available);

      if (newFiles.length > available) {
        setFileLimitError(true);
      }

      return [...prev, ...filesToAdd];
    });
    setValidating(false);
  }, []);

  const removeFile = (name: string) => {
    setFiles(prev => {
      const updated = prev.filter(f => f.file.name !== name);
      if (updated.length < MAX_FILES) setFileLimitError(false);
      return updated;
    });
  };

  const validFiles = files.filter(f => f.valid);
  const invalidFiles = files.filter(f => !f.valid);
  const isAtLimit = files.length >= MAX_FILES;

  return (
    <Container className="flex flex-col justify-between">
      <div className="pb-11">
        <BackButton />
        <h1 className="text-md sm:text-lg font-semi-bold uppercase text-primary-2">Manually connect your account</h1>
        <p className="text-base font-normal text-primary-2 mt-4">Please select files to upload.</p>
        <div className="mt-4 mb-6">
          <div className="mb-1 flex items-start">
            <FeatherIcon icon="check-circle" className="text-gray-500 mr-2" size={22} />
            <p>
              <strong>Supported formats:</strong> PDF
            </p>
          </div>
          <div className="mb-1 flex items-start">
            <FeatherIcon icon="check-circle" className="text-gray-500 mr-2" size={22} />
            <p>
              <strong>Max file size:</strong> up to 10 MB per file
            </p>
          </div>
          <div className="mb-1 flex items-start">
            <FeatherIcon icon="check-circle" className="text-gray-500 mr-2" size={22} />
            <p className="max-w-84 sm:max-w-full">
              <strong>In case of multiple files for same bank:</strong> Make sure you don't upload statements with
              period gaps between.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => !isAtLimit && inputRef.current?.click()}
          disabled={isAtLimit}
          className={`p-4 flex items-center justify-between relative w-full rounded-lg border-2 border-dashed transition-all duration-150 text-center ${
            isAtLimit
              ? 'border-stone-200 bg-stone-100 cursor-not-allowed opacity-60'
              : 'border-stone-300 bg-stone-50 active:bg-stone-100 active:border-stone-400 cursor-pointer'
          }`}
        >
          <input
            ref={inputRef}
            type="file"
            accept=".pdf,application/pdf"
            multiple
            className="hidden"
            onChange={e => {
              if (e.target.files?.length) processFiles(e.target.files);
              e.target.value = '';
            }}
          />
          <p className="text-stone-700 font-medium text-base">
            {isAtLimit ? `File limit reached (${MAX_FILES})` : 'Select files to upload'}
          </p>
          <div className="w-8 h-8 rounded-xl flex items-center justify-center bg-stone-200 text-stone-500">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="17 8 12 3 7 8" />
              <line x1="12" y1="3" x2="12" y2="15" />
            </svg>
          </div>

          {validating && (
            <div className="absolute inset-0 rounded-2xl bg-white/70 backdrop-blur-sm flex items-center justify-center">
              <div className="flex items-center gap-2 text-stone-600 font-medium">
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Validating...
              </div>
            </div>
          )}
        </button>

        {files.length > 0 && (
          <div className="mt-4 space-y-2">
            <p className="text-negative">
              {fileLimitError && `You can upload a maximum of ${MAX_FILES} files. Some files were not added.`}
              <br />
              {invalidFiles.length > 0 && 'Some files failed to upload. Please see details below and re-upload.'}
            </p>

            {invalidFiles.length > 0 && (
              <p className="text-primary-2 text-base">Failed to add ({invalidFiles.length})</p>
            )}

            {invalidFiles.map(file => (
              <FileComponent {...file} removeFile={removeFile} />
            ))}
            {validFiles.length > 0 && <p className="text-primary-2 text-base">Documents added ({validFiles.length})</p>}
            {validFiles.map(file => (
              <FileComponent {...file} removeFile={removeFile} />
            ))}
          </div>
        )}
      </div>
      <div
        className={`
            mt-4 grid grid-flow-row p-4 sm:px-0 sm:pb-0 gap-y-2 sm:gap-0 sm:grid-cols-auto 
            sm:grid-flow-col w-full bg-white border-t sm:border-0 border-gray-300 
            fixed sm:static bottom-0 left-0 sm:justify-between
          `}
      >
        <div className="sm:flex sm:gap-x-3 sm:w-96!">
          <PrimaryButton
            disabled={validFiles.length < 1 || invalidFiles.length > 0}
            onClick={() => history.push('document-uplaod-success')}
          >
            {t('Upload statements')}
          </PrimaryButton>
          <SecondaryButton
            className="sm:w-48! disabled:cursor-not-allowed! uppercase mt-2 sm:mt-0 font-medium! text-base! sm:text-sm! font-ABCReproMono!"
            btnType="outline"
            onClick={() => !isAtLimit && inputRef.current?.click()}
            disabled={isAtLimit}
          >
            {t('Add files')}
          </SecondaryButton>
        </div>
        <SecondaryButton onClick={() => exitApp()} className="border-none order-last sm:order-none">
          {t('Abort & return to partner')}
        </SecondaryButton>
      </div>
    </Container>
  );
};

export default DocumentUpload;
